import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Home, Copy, Download, Check, TrendingUp, Users, DollarSign } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const MarketplaceTools = ({ user }) => {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [selectedListings, setSelectedListings] = useState([]);
  const [generatedPost, setGeneratedPost] = useState("");
  const [collageUrl, setCollageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await axios.get(`${API}/api/listings`, {
        params: { status: "active", limit: 50 }
      });
      setListings(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const toggleListingSelection = (listingId) => {
    setSelectedListings(prev => 
      prev.includes(listingId) 
        ? prev.filter(id => id !== listingId)
        : [...prev, listingId]
    );
  };

  const generateSinglePost = (listing) => {
    const price = listing.price?.toLocaleString() || "Price Available";
    const beds = listing.bedrooms || "?";
    const baths = listing.bathrooms || "?";
    const sqft = listing.sqft?.toLocaleString() || "N/A";
    
    const template = `🏠 FSBO – No Commission! $${price}

${listing.title}
📍 ${listing.city}, Texas ${listing.zip_code || ''}

✨ ${beds} bed | ${baths} bath | ${sqft} sqft

💰 SAVE $${Math.round(listing.price * 0.06).toLocaleString()}+ in agent commissions!
🔑 Owner selling direct - serious buyers only
📸 ${listing.photos?.length || 0} professional photos
⚡ Move-in ready

${listing.description.substring(0, 200)}...

👉 See full details & photos: its-sold.com/listing/${listing.id}

#DFW #FSBO #${listing.city} #ForSaleByOwner #NoCommission #DallasHomes #TexasRealEstate`;

    return template;
  };

  const generateBulkPost = () => {
    const selected = listings.filter(l => selectedListings.includes(l.id));
    
    if (selected.length === 0) {
      toast.error("Select at least one listing");
      return;
    }

    const template = `🔥 ${selected.length} DFW Homes For Sale By Owner This Week!

💰 NO AGENT FEES | NO COMMISSIONS | DIRECT FROM OWNERS

${selected.slice(0, 9).map((listing, idx) => 
  `${idx + 1}. ${listing.city} - ${listing.bedrooms}bed/${listing.bathrooms}bath - $${listing.price?.toLocaleString()}`
).join('\n')}

✅ All listings verified & updated daily
✅ Professional photos & virtual tours  
✅ Save $15k-$40k per home in commissions
✅ Deal directly with motivated sellers

👉 View ALL ${selected.length} homes: its-sold.com

🎯 New listings added every day!
💬 Share this with anyone looking to buy or sell in DFW

#DFWRealEstate #FSBO #DallasHomes #FortWorthHomes #SaveMoney #ForSaleByOwner #DFWHomes #TexasHomes #NoCommission`;

    setGeneratedPost(template);
  };

  const generateBoostAd = (listing) => {
    const commission = Math.round(listing.price * 0.06);
    
    const template = `🏡 Owner selling direct – save $${(commission / 1000).toFixed(0)}k+ in commission!

${listing.bedrooms}bed | ${listing.bathrooms}bath | ${listing.sqft?.toLocaleString()} sqft
${listing.features?.includes('pool') ? '🏊 Pool | ' : ''}${listing.features?.includes('garage') ? '🚗 Garage | ' : ''}Updated Kitchen

📍 ${listing.city}, TX ${listing.zip_code}
💵 $${listing.price?.toLocaleString()}

✅ NO BUYER'S AGENT NEEDED
✅ Direct owner contact
✅ Flexible showings
✅ Ready to close fast

📞 Contact owner directly through Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup>
👉 its-sold.com/listing/${listing.id}

#${listing.city}Homes #DFW #FSBO #ForSaleByOwner`;

    return template;
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const generateCollage = async () => {
    if (selectedListings.length < 6) {
      toast.error("Select at least 6 listings for collage");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/marketplace/generate-collage`, {
        listing_ids: selectedListings.slice(0, 9)
      });
      
      setCollageUrl(response.data.collage_url);
      toast.success("Collage generated!");
    } catch (error) {
      toast.error("Failed to generate collage");
    } finally {
      setLoading(false);
    }
  };

  const demoTemplates = [
    {
      title: "Featured Listing Boost",
      description: "Best for promoting 1-2 hot properties ($15-$40 for 7 days)",
      icon: <TrendingUp className="w-6 h-6 text-orange-600" />
    },
    {
      title: "Weekly FSBO Roundup",
      description: "Post every 3-5 days showing multiple listings",
      icon: <Users className="w-6 h-6 text-blue-600" />
    },
    {
      title: "Price Drop Alert",
      description: "Highlight recent price reductions to grab attention",
      icon: <DollarSign className="w-6 h-6 text-green-600" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-blue-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <Home className="w-6 h-6 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Facebook Marketplace Tools</h1>
          <p className="text-lg text-gray-600">Generate optimized posts to drive massive traffic from Facebook</p>
        </div>

        {/* Strategy Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {demoTemplates.map((template, idx) => (
            <Card key={idx} className="p-6 border-2 border-blue-200">
              <div className="flex items-start space-x-3 mb-3">
                {template.icon}
                <h3 className="font-bold text-gray-900">{template.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{template.description}</p>
            </Card>
          ))}
        </div>

        {/* Listings Selection */}
        <Card className="p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Select Listings to Promote</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {listings.slice(0, 12).map((listing) => (
              <div
                key={listing.id}
                onClick={() => toggleListingSelection(listing.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  selectedListings.includes(listing.id)
                    ? 'border-blue-600 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm">{listing.title}</h3>
                    <p className="text-xs text-gray-600">{listing.city}, TX</p>
                  </div>
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedListings.includes(listing.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                  }`}>
                    {selectedListings.includes(listing.id) && <Check className="w-3 h-3 text-white" />}
                  </div>
                </div>
                <p className="text-lg font-bold text-blue-600">${listing.price?.toLocaleString()}</p>
                <p className="text-xs text-gray-500">{listing.bedrooms}bd | {listing.bathrooms}ba | {listing.sqft} sqft</p>
              </div>
            ))}
          </div>

          <div className="flex space-x-4">
            <Button
              onClick={generateBulkPost}
              disabled={selectedListings.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Generate Bulk Post ({selectedListings.length} listings)
            </Button>
            <Button
              onClick={generateCollage}
              disabled={selectedListings.length < 6}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Generate Collage Image
            </Button>
          </div>
        </Card>

        {/* Generated Posts */}
        {generatedPost && (
          <Card className="p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Generated Post</h2>
              <Button
                onClick={() => copyToClipboard(generatedPost, 'bulk')}
                variant="outline"
              >
                {copiedIndex === 'bulk' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copiedIndex === 'bulk' ? 'Copied!' : 'Copy'}
              </Button>
            </div>
            <Textarea
              value={generatedPost}
              onChange={(e) => setGeneratedPost(e.target.value)}
              rows={15}
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              💡 Tip: Post this every 3-5 days to Facebook Marketplace and DFW real estate groups
            </p>
          </Card>
        )}

        {/* Individual Listing Posts */}
        <Card className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Individual Listing Posts</h2>
          <div className="space-y-6">
            {listings.slice(0, 5).map((listing, idx) => (
              <div key={listing.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h3 className="font-bold text-lg">{listing.title}</h3>
                    <p className="text-sm text-gray-600">{listing.city}, TX | ${listing.price?.toLocaleString()}</p>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(generateSinglePost(listing), idx)}
                    size="sm"
                    variant="outline"
                  >
                    {copiedIndex === idx ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <Textarea
                  value={generateSinglePost(listing)}
                  readOnly
                  rows={8}
                  className="font-mono text-xs bg-white"
                />
                <div className="mt-3 flex space-x-3">
                  <Button
                    size="sm"
                    onClick={() => copyToClipboard(generateBoostAd(listing), `boost-${idx}`)}
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Copy Boost Ad Version
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MarketplaceTools;
